/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import Syphon from 'backbone.syphon';
import Ladda from 'ladda';

import InputListView from '@/views/InputListView';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import showErrorMessage from '@/util/showErrorMessage';
import { validateForm } from '@/util/ParsleyUtils';

import AWPackageContractExtensionCollectionView from '../AWPackageContractExtensionCollectionView';
import UpdateAWPackageInformationLegacy from '../../services/UpdateAWPackageInformationLegacy';
import CalculateDatesForAWPackage from '../../services/CalculateDatesForAWPackage';

import tmplLegacyAWPackageInformationView from './LegacyAWPackageInformationView.hbs';

const LegacyAWPackageInformationView = View.extend({
  template: tmplLegacyAWPackageInformationView,

  ui: {
    informationSection: '.information-section',
    btnDateCalc: '.btn-calculate-dates',
    contractExtensionContainer: '.contract-extension-container',
    commandsContainer: '.commands-container',
    crsContainer: '.crs-container',
    koStatusDateReq: 'input[name="koStatusDateReq"]',
    submittedOn: 'input[name="submittedOn"]',
    contractStartDate: 'input[name="contractStartDate"]',
    contractEndDate: 'input[name="contractEndDate"]'
  },

  regions: {
    contractExtensionContainer: '@ui.contractExtensionContainer',
    commandsContainer: '@ui.commandsContainer',
    crsContainer: '@ui.crsContainer'
  },

  events: {
    'click .add-contract-extension': 'addContractExtension',
    'click @ui.btnDateCalc': 'onClickDateCalc'
  },

  onRender() {
    const awPackage = this.model.get('awPackage');

    this.ui.koStatusDateReq.datepicker({ format: 'mm/dd/yyyy' });
    this.ui.koStatusDateReq.datepicker('setDate', awPackage.koStatusDateReq);

    this.ui.submittedOn.datepicker({ format: 'mm/dd/yyyy' });
    this.ui.submittedOn.datepicker('setDate', awPackage.submittedOn);

    this.ui.contractStartDate.datepicker({ format: 'mm/dd/yyyy' });
    this.ui.contractStartDate.datepicker('setDate', awPackage.contractStartDate);

    this.ui.contractEndDate.datepicker({ format: 'mm/dd/yyyy' });
    this.ui.contractEndDate.datepicker('setDate', awPackage.contractEndDate);

    this.awPackageContractExtensions = new AWPackageContractExtensionCollectionView({
      collection: new Backbone.Collection(awPackage.awPackageContractExtensions),
      model: new Backbone.Model({
        contractExtensionTypes: this.model.get('contractExtensionTypes'),
        editable: this.model.get('editable')
      })
    });
    this.showChildView('contractExtensionContainer', this.awPackageContractExtensions);

    this.showChildView(
      'commandsContainer',
      new InputListView({
        model: new Backbone.Model({
          label: 'All Commands in AWP',
          ratio: 'half',
          type: 'text',
          inputName: 'commandName',
          isReadOnly: true,
          emptyText: 'No Commands Found'
        }),
        collection: new Backbone.Collection(awPackage.commands)
      })
    );

    this.showChildView(
      'crsContainer',
      new InputListView({
        model: new Backbone.Model({
          label: 'All Assigned CRSs',
          ratio: 'half',
          type: 'text',
          inputName: 'name',
          isReadOnly: true,
          emptyText: 'No CRSs Found'
        }),
        collection: new Backbone.Collection(awPackage.crss)
      })
    );

    Syphon.deserialize(this.ui.informationSection, awPackage);

    if (!this.model.get('editable')) {
      this.toggleForm(false);
    }
  },

  toggleForm(state) {
    this.$el.find(':input').prop('disabled', !state);
  },

  addContractExtension(e) {
    e.preventDefault();
    this.getChildView('contractExtensionContainer').options.collection.add({});
  },

  showDateCalcError() {
    showErrorMessage({
      errorCode: 'dateCalcError',
      errorTitle: 'Error Calculating KO Status Date',
      errorMessage: 'An error occurred while calculating the required KO status date.'
    });
  },

  showUnauthorizedDateCalcError(message) {
    showErrorMessage({
      errorCode: 'dateCalcError',
      errorTitle: 'Unauthorized User',
      errorMessage: `${message}, unable to calculate KO Status Date.`
    });
  },

  async onClickDateCalc(e) {
    e.preventDefault();

    const spinner = Ladda.create(this.ui.btnDateCalc[0]);
    spinner.start();

    try {
      const koStatusDateReq = await CalculateDatesForAWPackage(this.model.get('awPackage').id);
      this.ui.koStatusDateReq.val(koStatusDateReq);
    } catch (err) {
      if (err?.message?.string === 'Unauthorized') {
        this.showUnauthorizedDateCalcError(err.message.detail);
      } else {
        this.showDateCalcError();
      }
    } finally {
      spinner.stop();
    }
  },

  async onSave() {
    if (!this.model.get('editable')) {
      return;
    }

    const formData = Syphon.serialize(this.ui.informationSection);
    formData.awPackageContractExtensions = this.awPackageContractExtensions.getFormData();
    formData.awPackageId = this.model.get('awPackage').id;

    try {
      await UpdateAWPackageInformationLegacy(formData);
      showSuccessMessage({
        code: 'saveSuccess',
        title: 'Save Success',
        message: 'AW Package Information Saved.',
        hideAfter: 1
      });
    } catch (err) {
      showErrorMessage({
        errorCode: 'saveError',
        errorTitle: 'Save Error',
        errorMessage: 'Error Saving File.'
      });
      throw new Error(err);
    }
  }
});

export default LegacyAWPackageInformationView;
